import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import { useTheme } from '@mui/material/styles';
import { DetailedList } from '@qbe/ads-blocks-forms';
import { Divider, IconButton, Stack, Typography } from '@qbe/ads-core';
import React, { useState } from 'react';

import UpsertInterestedParty from '~/components/UpsertInterestedParty';
import Modal from '~/components-ds/Modal';
import { useAppContent, useAppDispatch, useExtraInfo } from '~/hooks';
import { useCoverOptions } from '~/hooks/useCoverOptions';
import { useOpenClose } from '~/hooks/useOpenClose';
import { removeInterestedParty } from '~/slices/extraInfo';
import { InterestedPartyUI as InterestedPartyType } from '~/types/policy';
import { interestedPartyUtils, sortCovers } from '~/utils';

const InterestedParties = () => {
  const { palette } = useTheme();
  const dispatch = useAppDispatch();
  const { interestedParties } = useExtraInfo();
  const { data: coverOptions } = useCoverOptions();
  const { open: showEditModal, close: closeEditModal, isOpen: isOpenEditModal } = useOpenClose();
  const [selectedParty, setSelectedParty] = useState<InterestedPartyType | null>(null);

  const { getTextContent: getSharedContent } = useAppContent({
    basePath: 'shared',
  });
  const { getTextContent } = useAppContent({
    basePath: 'dsme.extraInfoMoreBusinessDetailsLandlord',
  });

  if (interestedParties?.length === 0) return null;

  const interestedPartiesCovers = sortCovers(
    interestedParties!.map((ip) => ip.cover).filter(interestedPartyUtils.onlyUnique),
    coverOptions
  );

  const getActionIconButtons = (curr: InterestedPartyType) => (
    <Stack direction="row" columnGap="0.5rem">
      <IconButton
        iconProps={{
          muiIcon: EditIcon,
          color: palette.text.primary,
        }}
        onClick={() =>
          onEdit({
            id: curr.id,
            cover: curr.cover,
            name: curr.name,
          })
        }
        name="edit"
      />
      <Divider
        orientation="vertical"
        sx={{
          border: '1px solid divider',
          height: '20px',
          boxSizing: 'border-box',
        }}
      />
      <IconButton
        iconProps={{
          muiIcon: ClearIcon,
          color: palette.text.primary,
        }}
        onClick={() => onRemove(curr.id)}
        name="clear"
      />
    </Stack>
  );

  const onRemove = (id: string) => {
    dispatch(removeInterestedParty(id));
  };

  const UPDATE_INTERESTED_PARTY_FORM_ID = 'update-interested-party-form';

  const onEdit = (interestedParty: InterestedPartyType) => {
    showEditModal();
    setSelectedParty(interestedParty);
  };

  return (
    <>
      {interestedPartiesCovers?.map((cover) => (
        <React.Fragment key={cover}>
          <Typography component="h4" fontWeight="fontWeightBold">
            {cover}
          </Typography>
          <DetailedList
            list={
              // TODO: Need to verify
              interestedParties
                ?.filter((ip) => ip.cover === cover)
                ?.map((ip) => ({
                  id: ip.id,
                  name: ip.name,
                  label: getTextContent('content.interestedPartiesDetailList.title'),
                  value: ip.name,
                  stackActions: getActionIconButtons(ip),
                })) ?? []
            }
            variant="stack"
            // TODO - why Typography header at L81-83
            title={cover} // @TODO can be fixed by https://qbe-appservices.atlassian.net/browse/AD1-549
            key={cover}
          />
        </React.Fragment>
      ))}

      {isOpenEditModal && (
        <Modal
          id="update-interested-party-modal"
          open
          primaryBtnProps={{
            label: getTextContent('buttons.updateParty'),
            type: 'submit',
            form: UPDATE_INTERESTED_PARTY_FORM_ID,
            'data-track': 'Update - interested party',
          }}
          secondaryBtnProps={{
            label: getSharedContent('backModalButtonLabel'),
            'data-track': 'Back - interested party',
            onClick: closeEditModal,
          }}
          title={getTextContent('content.interestedPartiesModal.title')}
        >
          <UpsertInterestedParty
            mode="update"
            selectedParty={selectedParty!}
            onUpdateParty={closeEditModal}
            onClose={closeEditModal}
            formId={UPDATE_INTERESTED_PARTY_FORM_ID}
          />
        </Modal>
      )}
    </>
  );
};

export default InterestedParties;
